// web/evolutionBox.ts — the PLAYER-FACING Evolution Box economy.
//
// BROOD's progression fantasy: play → earn Mutation Energy → fill a box → open it
// for guaranteed gene rewards, with rare tiers that feel like a moment. This is a
// thin, DETERMINISTIC economy layered over the engine's gene forge — it mints real
// genes into the player's inventory (so boxes feed the genome/apply loop) and is
// reproducible from (seed + the ordered list of opens), exactly like the rest of
// the save/replay model. No DOM here: the class is pure logic so it can be unit
// tested headlessly (web/evolutionBox.test.mjs); the UI lives in ui.ts/main.ts.
//
// ENERGY is a pure function of MONOTONIC, eviction-proof sim counters (kills,
// territory wars, hive nodes, region unlocks, leaderboard lifetime) plus a banked
// bonus from duplicate conversions, minus what's been spent on boxes. Because every
// input only ratchets up, current energy never silently drops and replay rebuilds
// it exactly.

import { NIL, RARITY } from '../src/types.ts';
import type { GeneInventory, GeneId } from '../src/sim/genes.ts';
import { Rng } from '../src/rng.ts';
import { forgeGene, forgeBareGene, GENE_SOURCE_EVENT } from '../src/sim/geneForge.ts';
import { RARITY_NAME, GENE_TYPE_NAME, TRAIT_MAGNITUDE } from '../src/sim/geneData.ts';
import { genePowerOf } from '../src/sim/genePower.ts';
import type { PowerComparison } from '../src/sim/genePower.ts';
import type { GeneAdvice } from '../src/sim/advisor.ts';
import { rewardWindow } from '../src/sim/geneLootScaling.ts';
import { boxTierWeights, rollUpgradeSlot, weightTotal } from '../src/sim/upgradePacing.ts';
import type { UpgradeTier } from '../src/sim/upgradePacing.ts';

/** The five box tiers, ascending rarity (index = tier). */
export const BOX_TIERS: readonly string[] = [
  'Adaptation', 'Mutation', 'Aberration', 'Apex', 'Evolutionary Miracle',
];
export const TIER_COUNT = BOX_TIERS.length;
/** Aberration is the first "high" tier (resets soft pity); Apex the first "epic". */
const TIER_ABERRATION = 2;
const TIER_APEX = 3;

/** The globally lowest-magnitude trait — the FINEST per-stack Power step there is. Relative
 *  loot fitting climbs with it so a reward can settle inside even a narrow window. */
const FINE_TRAIT = ((): number => {
  let best = 0;
  for (let t = 1; t < TRAIT_MAGNITUDE.length; t++) {
    if ((TRAIT_MAGNITUDE[t] as number) < (TRAIT_MAGNITUDE[best] as number)) best = t;
  }
  return best;
})();

// --- Tuning (progression balance). All integer; see PLAYTESTING.md. ----------

/** Energy weights per monotonic source. */
const W_KILL = 3;       // per enemy/wildlife kill credited to the player
const W_WAR = 16;       // per territory war won
const W_HIVE = 22;      // per founded hive node
const W_REGION = 18;    // per region unlocked (world progression)
const W_PROJECT = 80;   // per region-scale project completed (a major long-term feat)
const BOARD_DIV = 8;    // leaderboard peak-score is divided down into energy
const BOARD_MAX = 300;  // HARD CAP on the standing term — peakAt can grow huge on a
                        // dominant colony, so cap its energy contribution to keep the
                        // economy honest (boxes/ascension stay something you earn).

/** Box cost grows with opens so late boxes still feel earned (capped). */
const COST_BASE = 90;
const COST_STEP = 20;
const COST_CAP = 240;

/** Base tier weights (Adaptation…Miracle), out of their sum. */
const BASE_WEIGHTS: readonly number[] = [560, 280, 110, 38, 12];
/** Soft pity: after this many opens with no Aberration+, shift weight upward. */
const SOFT_PITY_START = 3;
/** Hard pity: guarantee an Aberration+ by this drought; an Apex+ by the longer one. */
const HARD_PITY_ABERRATION = 10;
const HARD_PITY_APEX = 24;

/** Rewards per tier. */
const GUARANTEED_GENES: readonly number[] = [1, 1, 2, 2, 3];
const QUALITY_BONUS: readonly number[] = [0, 1, 2, 3, 5];   // extra forge rarity draws
/** Rarity FLOOR a tier guarantees (−1 = none). Aberration→Rare, Apex→Epic, Miracle→Legendary. */
const RARITY_FLOOR: readonly number[] = [-1, -1, RARITY.Rare, RARITY.Epic, RARITY.Legendary];
/** Energy refunded per rarity when a duplicate is auto-converted. */
const SACRIFICE_YIELD: readonly number[] = [3, 6, 12, 24, 48];
/** Fallback energy if a reward can't be inserted even after eviction (never wasted). */
const FULL_FALLBACK_ENERGY = 20;

export interface ProgressSignals {
  kills: number;
  warsWon: number;
  hives: number;
  regionsUnlocked: number;
  /** Region-scale projects completed (a major long-term evolution opportunity). */
  projectsCompleted: number;
  /** Leaderboard standing (a bounded, monotonic peak score — see game.ts). */
  standing: number;
}

export interface RewardGene {
  label: string;
  type: number;
  rarity: number;
  duplicate: boolean;
  /** Power Score of this freshly-forged gene (display-only; ascension 0 for box genes). */
  power: number;
  /** Stable serial of the gene, so the comparison can exclude it from the incumbent scan. */
  serial: number;
  /** Total mutation-trait stacks on the gene — feeds the collectible card's human stat
   *  lines (web/geneCard.ts). Display-only; never affects the drop economy or hash. */
  traitTotal: number;
  /** Reward-vs-incumbent verdict, attached later by game.ts (which knows ascension). */
  comparison?: PowerComparison;
  /** Forecast: the % of its natural caste's CURRENT power this reward would add when
   *  applied (the relative power-progression budget). Attached by game.ts; undefined for a
   *  duplicate converted to energy. Display-only. */
  gainPct?: number;
  /** The PACED upgrade magnitude this reward was generated to hit (sidegrade…exceptional),
   *  set on the RELATIVE (player) path only — undefined on the off-path. Display-only; the
   *  honest bucket label for the upgrade-magnitude distribution (src/sim/upgradePacing.ts). */
  upgradeTier?: UpgradeTier;
  /** Evolution Advisor FIT verdict (does this gene address the colony's needs/identity?),
   *  attached by game.ts (which has the diagnostics + identity). Display-only; same badge the
   *  inventory rows show, surfaced on the reveal so Equip/Sacrifice/Store reads at a glance. */
  advice?: GeneAdvice;
}

export interface OpenResult {
  openIndex: number;
  tier: number;
  tierName: string;
  genes: RewardGene[];
  refunded: number;
  /** Genes destroyed/converted to energy during this open (evicted + fallback + duplicates) —
   *  so the profile's "genes sacrificed" stat counts box conversions, not just fusions. */
  sacrificed: number;
  cost: number;
}

export interface EnergyBreakdown {
  kill: number;
  war: number;
  hive: number;
  region: number;
  project: number;
  board: number;
  bonus: number;
  earned: number;
  spent: number;
  current: number;
}

export interface BoxState {
  current: number;
  cost: number;
  progress: number;   // 0..1
  ready: boolean;
  tier: number;       // previewed tier of the next box
  tierName: string;
  opened: number;
  pityAberration: number;
  pityApex: number;
}

/**
 * Deterministically roll a tier from (per-open rng + pity). ALWAYS consumes
 * exactly one draw so a preview and the real open agree and the forge draws that
 * follow stay aligned. Pity shifts weight up (soft) and floors the result (hard).
 */
export function rollTier(rng: Rng, pityAberration: number, pityApex: number): number {
  const w = BASE_WEIGHTS.slice();
  const soft = pityAberration - SOFT_PITY_START;
  if (soft > 0) {
    w[TIER_ABERRATION] = (w[TIER_ABERRATION] as number) + soft * 22;
    w[TIER_APEX] = (w[TIER_APEX] as number) + soft * 9;
    w[4] = (w[4] as number) + soft * 2;
  }
  let total = 0;
  for (let i = 0; i < TIER_COUNT; i++) total += w[i] as number;
  let r = rng.int(total);
  let tier = 0;
  for (let i = 0; i < TIER_COUNT; i++) {
    r -= w[i] as number;
    if (r < 0) { tier = i; break; }
  }
  // Hard pity floors (pity counts the CURRENT drought; +1 includes this open).
  if (pityAberration + 1 >= HARD_PITY_ABERRATION && tier < TIER_ABERRATION) tier = TIER_ABERRATION;
  if (pityApex + 1 >= HARD_PITY_APEX && tier < TIER_APEX) tier = TIER_APEX;
  return tier;
}

/**
 * Pure cumulative-weight caste pick for ADAPTIVE loot. Given a per-caste need
 * vector and `r` in `[0, sum(needWeights))`, walk the cumulative weights and
 * return the chosen caste index. Unit-testable without RNG. Defensive: an
 * out-of-range `r` (sum 0, or r past the end) returns the last caste so the
 * caller never gets -1.
 */
export function pickAdaptiveCaste(needWeights: readonly number[], r: number): number {
  let acc = r;
  for (let c = 0; c < needWeights.length; c++) {
    acc -= needWeights[c] as number;
    if (acc < 0) return c;
  }
  return needWeights.length - 1;
}

export class BoxEconomy {
  private readonly inv: GeneInventory;
  private readonly seed: number;
  private readonly colony: number;

  /** History of opens (also the source of `opened` count + pity replay). */
  private readonly opens: OpenResult[] = [];
  /** Energy banked from duplicate conversions (deterministic via replay). */
  private bonusEnergy = 0;
  /** Energy spent on boxes so far. */
  private spentEnergy = 0;
  /** Opens since the last Aberration+ / Apex+ (drive pity). */
  private pityAberration = 0;
  private pityApex = 0;

  constructor(inv: GeneInventory, seed: number, colony = 0) {
    this.inv = inv;
    this.seed = seed >>> 0;
    this.colony = colony;
  }

  /** Independent per-open RNG (never touches the sim's stream). */
  private rngFor(openIndex: number): Rng {
    return new Rng((Math.imul(openIndex + 1, 0x9e3779b9) ^ this.seed) >>> 0);
  }

  /** Cost of the n-th box (0-based), escalating then capped. */
  private costFor(n: number): number {
    return Math.min(COST_BASE + n * COST_STEP, COST_CAP);
  }

  nextCost(): number {
    return this.costFor(this.opens.length);
  }

  /** Energy purely from progression signals (monotonic) + bank − spend. */
  breakdown(s: ProgressSignals): EnergyBreakdown {
    // Clamp every source ≥ 0 — defends against any upstream Int32 overflow so
    // current energy can never go silently negative.
    const kill = W_KILL * Math.max(0, s.kills);
    const war = W_WAR * Math.max(0, s.warsWon);
    const hive = W_HIVE * Math.max(0, s.hives);
    const region = W_REGION * Math.max(0, s.regionsUnlocked);
    const project = W_PROJECT * Math.max(0, s.projectsCompleted);
    const board = Math.min(Math.floor(Math.max(0, s.standing) / BOARD_DIV), BOARD_MAX);
    const earned = kill + war + hive + region + project + board + this.bonusEnergy;
    return {
      kill, war, hive, region, project, board, bonus: this.bonusEnergy,
      earned, spent: this.spentEnergy, current: earned - this.spentEnergy,
    };
  }

  energy(s: ProgressSignals): number {
    return this.breakdown(s).current;
  }

  /** Spend energy outside box-opening (e.g. gene ascension). Replayed via actions. */
  spend(amount: number): void {
    if (amount > 0) this.spentEnergy += amount;
  }

  /** Previewed tier of the next box (deterministic; no state change). */
  previewTier(): number {
    return rollTier(this.rngFor(this.opens.length), this.pityAberration, this.pityApex);
  }

  state(s: ProgressSignals): BoxState {
    const current = this.energy(s);
    const cost = this.nextCost();
    const tier = this.previewTier();
    return {
      current,
      cost,
      progress: cost > 0 ? Math.max(0, Math.min(1, current / cost)) : 1,
      ready: current >= cost,
      tier,
      tierName: BOX_TIERS[tier] as string,
      opened: this.opens.length,
      pityAberration: this.pityAberration,
      pityApex: this.pityApex,
    };
  }

  /** Recent opens, newest first. */
  history(limit = 8): OpenResult[] {
    const out = this.opens.slice(Math.max(0, this.opens.length - limit));
    out.reverse();
    return out;
  }

  opensCount(): number {
    return this.opens.length;
  }

  /**
   * Apply one box open (deterministic). The CALLER spends nothing itself — this
   * records the cost. `keepDuplicates` keeps dupes instead of converting them.
   * Mints guaranteed genes via the engine forge (floored up by tier), converts
   * duplicates to banked energy, evicts the weakest gene if the inventory is full
   * so a reward is never lost, and advances pity. Returns the result for the UI.
   */
  open(
    tick: number,
    keepDuplicates: boolean,
    needWeights?: readonly number[],
    adaptivePct = 70,
    baselineForType?: (type: number) => number,
  ): OpenResult {
    const openIndex = this.opens.length;
    // RELATIVE LOOT (src/sim/geneLootScaling.ts): when the caller supplies per-slot
    // baselines, forge each reward into a rarity WINDOW around the player's current power
    // so loot is an increment, not a jackpot. Absent ⇒ the classic absolute forge (the
    // off-path stays byte-identical — see web/adaptiveLoot.test.mjs).
    const relative = baselineForType !== undefined;
    const rng = this.rngFor(openIndex);
    const tier = rollTier(rng, this.pityAberration, this.pityApex);
    const cost = this.costFor(openIndex);

    const guaranteed = GUARANTEED_GENES[tier] as number;
    const quality = QUALITY_BONUS[tier] as number;
    const floor = RARITY_FLOOR[tier] as number;
    const genes: RewardGene[] = [];
    let refunded = 0;
    let sacrificed = 0; // genes converted to energy this open (evictions + fallbacks + duplicates)

    for (let g = 0; g < guaranteed; g++) {
      let id = this.forgeOne(rng, tick, quality, needWeights, adaptivePct, relative);
      if (id === NIL) {
        // Inventory full — evict the weakest gene (refunding it), then retry once.
        const victim = this.weakestGene();
        if (victim !== NIL) {
          refunded += SACRIFICE_YIELD[this.inv.rarityOf(victim)] as number;
          this.inv.retire(victim);
          sacrificed++;
          id = this.forgeOne(rng, tick, quality, needWeights, adaptivePct, relative);
        }
      }
      if (id === NIL) {
        // Still no room: convert the reward to energy so the box never wastes a drop.
        refunded += FULL_FALLBACK_ENERGY;
        sacrificed++;
        continue;
      }
      let upgradeTier: UpgradeTier | undefined;
      if (relative) {
        // PACING (src/sim/upgradePacing.ts): the player path forces each reward's FINAL rarity
        // to a paced UPGRADE TIER drawn from the per-open rng, so a long run of opens hits the
        // designed magnitude distribution (≈40/35/15/7/3 at the reference box tier) instead of the
        // forge's raw rarity table. The box tier SHIFTS the weights upward (more major/exceptional)
        // so advancing boxes still feel better. One rng draw (always, in fixed order). This SET
        // supersedes the off-path RARITY_FLOOR promote — the pace already lands well above any floor.
        const weights = boxTierWeights(tier);
        const slot = rollUpgradeSlot(rng.int(weightTotal(weights)), weights);
        this.inv.setRarity(id, slot.rarity);
        upgradeTier = slot.tier;
        // Fit the (bare) reward to the paced tier's POWER window around the player's baseline. The
        // `band` is the tier's REWARD_RATIO_BAND index (== rarity for every tier except the top
        // `exceptional`, which uses the Apex band so a storable Legendary reaches the bigger Apex
        // window). Window matches the final rarity/band; the off-path forged its traits and skips this.
        this.fitRewardToWindow(id, rng, baselineForType as (type: number) => number, slot.band);
      } else if (floor >= 0 && (this.inv.rarityOf(id) as number) < floor) {
        this.inv.promoteToRarity(id, floor);
      }
      const type = this.inv.typeOf(id) as number;
      const rarity = this.inv.rarityOf(id) as number;
      const label = this.inv.describeGene(id);
      // Capture Power + serial BEFORE any duplicate-retire — the id is still live here.
      // Box genes are freshly forged ⇒ ascension 0, so no progression level to pass.
      const power = genePowerOf(this.inv, id);
      const serial = this.inv.serialOf(id) as number;
      const traitTotal = this.inv.traitTotalOf(id) as number;
      const isDup = !keepDuplicates && this.isDuplicate(type, rarity, id);
      const reward: RewardGene = { label, type, rarity, duplicate: isDup, power, serial, traitTotal };
      // Attach the paced tier ONLY on the relative path (it is undefined off-path), so the
      // off-path RewardGene shape stays byte-identical to before this feature existed.
      if (upgradeTier !== undefined) reward.upgradeTier = upgradeTier;
      if (isDup) {
        refunded += SACRIFICE_YIELD[rarity] as number;
        this.inv.retire(id);
        sacrificed++;
      }
      genes.push(reward);
    }

    this.bonusEnergy += refunded;
    this.spentEnergy += cost;

    // Advance pity (reset on the relevant tier).
    this.pityAberration = tier >= TIER_ABERRATION ? 0 : this.pityAberration + 1;
    this.pityApex = tier >= TIER_APEX ? 0 : this.pityApex + 1;

    const result: OpenResult = {
      openIndex, tier, tierName: BOX_TIERS[tier] as string, genes, refunded, sacrificed, cost,
    };
    this.opens.push(result);
    return result;
  }

  /**
   * Sacrifice a held gene (by stable SERIAL) → banked Mutation Energy, at the same
   * yield as the box's duplicate conversion. Replay-safe: the caller records a
   * `sacrifice` action, and serials are deterministic, so replay reproduces it.
   * Returns the energy gained (0 if the serial isn't a live owned gene).
   */
  sacrifice(serial: number): number {
    for (let id = this.inv.firstOf(this.colony); id !== NIL; id = this.inv.nextOf(id)) {
      if ((this.inv.serialOf(id) as number) === serial) {
        const gain = SACRIFICE_YIELD[this.inv.rarityOf(id) as number] as number;
        this.inv.retire(id);
        this.bonusEnergy += gain;
        return gain;
      }
    }
    return 0;
  }

  // --- helpers ---------------------------------------------------------------

  /**
   * Forge one box gene. Two paths, both bumping `eventDropsTotal` exactly as the
   * old `awardGeneFromEvent` did (so counters/hashes stay consistent):
   *
   *  - OFF-PATH (`needWeights` absent or empty): behaves EXACTLY like today's
   *    `awardGeneFromEvent` — forges from the EVENT sentinel (uniform type) and
   *    makes ZERO extra rng draws, so the off-path RNG stream is byte-identical to
   *    before this feature existed (headless / tests are unaffected).
   *
   *  - ON-PATH (`needWeights` present, sum > 0): draws the adaptive/random decision
   *    FIRST (`rng.int(100) < adaptivePct`); if adaptive, weighted-picks a needy
   *    caste (`rng.int(totalNeed)`) and forges with THAT caste as the source so the
   *    existing `CASTE_TYPE_WEIGHT` bias shapes the body-part type. If not adaptive,
   *    forges uniform via the EVENT sentinel. The decision draw + optional caste
   *    draw are the ONLY additions, always in this fixed order.
   */
  private forgeOne(
    rng: Rng,
    tick: number,
    quality: number,
    needWeights?: readonly number[],
    adaptivePct = 70,
    bare = false,
  ): GeneId {
    let sourceCaste = GENE_SOURCE_EVENT;
    if (needWeights !== undefined) {
      let totalNeed = 0;
      for (let i = 0; i < needWeights.length; i++) totalNeed += needWeights[i] as number;
      if (totalNeed > 0) {
        // Draw 1 (fixed order): adaptive vs fully-random.
        const adaptive = rng.int(100) < adaptivePct;
        if (adaptive) {
          // Draw 2 (only when adaptive): weighted caste pick by need.
          sourceCaste = pickAdaptiveCaste(needWeights, rng.int(totalNeed));
        }
        // else: fall through with the EVENT sentinel ⇒ uniform type (no 2nd draw).
      }
    }
    // `bare` (relative-loot path): forge type+rarity only — the caller fits the traits to a
    // power window. Otherwise the classic forge rolls traits here (byte-identical off-path).
    const id = bare
      ? forgeBareGene(this.inv, rng, this.colony, this.colony, sourceCaste, tick, quality)
      : forgeGene(this.inv, rng, this.colony, this.colony, sourceCaste, tick, quality);
    if (id !== NIL) this.inv.eventDropsTotal++;
    return id;
  }

  /**
   * Fit a freshly forged BARE reward to its rarity power-WINDOW around the player's current
   * baseline (src/sim/geneLootScaling.ts): draw an in-window POSITION, then stack the globally
   * lowest-magnitude trait — the finest Power-Score step available — until the gene reaches the
   * target, undoing any stack (incl. the first) that would breach the ceiling.
   *
   * The result is a reward whose power TRACKS the player's current gear instead of a flat
   * rarity floor, so loot reads as a steady step rather than a lottery jackpot. It is NOT exact:
   * the engine amplifies a gene's expression by rarity (`GeneInventory.expressGene`), so a high
   * rarity's single stack is a coarse, baseline-independent jump — bigger than a narrow window —
   * meaning high-rarity rewards may settle a little ABOVE their window (and very low-rarity ones
   * a little below). Both stay a modest multiple of the baseline; only player BOX loot is fitted.
   *
   * Deterministic: ONE position draw, then RNG-free stacking, so the live open and a save-replay
   * forge the identical gene.
   *
   * `band` (the upgrade-pacing REWARD_RATIO_BAND index) overrides which window is used when supplied
   * — for the top `exceptional` tier it is the Apex band, so a storable Legendary gene reaches the
   * bigger Apex POWER window without an unstorable rarity. Absent, the gene's own rarity selects it.
   */
  private fitRewardToWindow(
    id: GeneId,
    rng: Rng,
    baselineForType: (type: number) => number,
    band?: number,
  ): void {
    const type = this.inv.typeOf(id) as number;
    const windowTier = band !== undefined ? band : (this.inv.rarityOf(id) as number);
    const { lo, hi } = rewardWindow(baselineForType(type), windowTier);
    const pos = rng.int(1001) / 1000;
    const target = Math.round(lo + pos * (hi - lo)); // a point inside the window
    const MAX_STACKS = 80; // guard: a huge baseline can't pump a gene without bound.
    for (let n = 0; n < MAX_STACKS && genePowerOf(this.inv, id) < target; n++) {
      this.inv.addTrait(id, FINE_TRAIT, 1);
      if (genePowerOf(this.inv, id) > hi) { this.inv.addTrait(id, FINE_TRAIT, -1); break; }
    }
    // Guarantee a real, mutated gene even when the first (rarity-amplified) stack already
    // overshot a narrow window — the gene stays a steady step from the baseline regardless.
    if ((this.inv.traitTotalOf(id) as number) === 0) this.inv.addTrait(id, FINE_TRAIT, 1);
  }

  /** True if the player already holds ANOTHER gene of this (type, rarity). */
  private isDuplicate(type: number, rarity: number, exclude: GeneId): boolean {
    for (let id = this.inv.firstOf(this.colony); id !== NIL; id = this.inv.nextOf(id)) {
      if (id === exclude) continue;
      if ((this.inv.typeOf(id) as number) === type && (this.inv.rarityOf(id) as number) === rarity) {
        return true;
      }
    }
    return false;
  }

  /** Weakest held gene (lowest rarity, then fewest traits, then lowest serial), or NIL. */
  private weakestGene(): GeneId {
    let worst: GeneId = NIL;
    let wr = 99, wt = 1e9, ws = 1e9;
    for (let id = this.inv.firstOf(this.colony); id !== NIL; id = this.inv.nextOf(id)) {
      const r = this.inv.rarityOf(id) as number;
      const t = this.inv.traitTotalOf(id) as number;
      const s = this.inv.serialOf(id) as number;
      if (r < wr || (r === wr && (t < wt || (t === wt && s < ws)))) {
        worst = id; wr = r; wt = t; ws = s;
      }
    }
    return worst;
  }
}

/** Friendly one-line reward label (rarity + body-part type) for the reveal/history. */
export function shortRewardName(type: number, rarity: number): string {
  return `${RARITY_NAME[rarity] as string} ${GENE_TYPE_NAME[type] as string}`;
}
